import React, {useState, useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Joyride, {CallBackProps, Step, Styles, type FloaterProps, EVENTS, STATUS} from 'react-joyride';

import OnboardingDialog from '../shared/molecule/onboarding/OnboardingDialog';
import AppTourChecklist from '../shared/molecule/onboarding/AppTourChecklist';
import {createFreeCustomTooltipWrapper} from './FreeCustomTooltip';

interface AppTourProps extends Partial<FloaterProps> {
  steps: Step[];
  modalTitle?: string;
  modalDescription?: string;
  modalActionLabel?: string;
  onModalAction: () => void;
  completedStepsRef: React.MutableRefObject<number>;
}

const checklistSteps = [
  {label: 'Introduction to the Privacy Dashboard.'},
  {label: 'How to improve profile scans.'},
  {label: 'Save and scan your detailed profile!'},
  {label: 'Complete the onboarding.'}
];

const tourStyles: Partial<Styles> = {
  options: {
    primaryColor: '#F3F3F3',
    backgroundColor: 'rgba(0,0,0,0.85)',
    textColor: '#fff',
    zIndex: 2100,
  },
  buttonBack: {
    display: 'none',
  },
  buttonSkip: {
    display: 'none',
  },
};

const ProfileAppTourComponent: React.FC<AppTourProps> = ({
                                                           steps,
                                                           modalTitle,
                                                           modalDescription,
                                                           modalActionLabel,
                                                           onModalAction,
                                                           completedStepsRef
                                                         }) => {
  const navigate = useNavigate();
  const [showChecklist, setShowChecklist] = useState(false);
  const [showTour, setShowTour] = useState(true);
  const [showStep1Modal, setShowStep1Modal] = useState(false);
  const [showStep2Modal, setShowStep2Modal] = useState(false);
  const [showFinalModal, setShowFinalModal] = useState(false);

  const locale = useMemo(() => ({
    last: 'Finish',
    next: 'Next',
  }), []);

  const floaterProps = useMemo(() => ({
    hideArrow: true,
  }), []);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const {status, index, type} = data;

    if (type === EVENTS.STEP_AFTER && typeof index === 'number') {
      if (index === 0) {
        setShowTour(false);
        setShowStep1Modal(true);
      }

      if (index === 1) {
        setShowTour(false);
        setShowStep2Modal(true);
      }

    }

    if ((status === STATUS.FINISHED || status === STATUS.SKIPPED) && type === EVENTS.TOUR_END) {
      navigate('/dashboard', {state: {fromTour: true}});
      completedStepsRef.current++;
      setShowTour(false);
      setShowFinalModal(true);
    }
  }, [navigate, completedStepsRef]);

  return (
    <>
      <Joyride
        steps={steps}
        run={showTour}
        continuous
        showSkipButton={false}
        tooltipComponent={createFreeCustomTooltipWrapper()}
        showProgress={false}
        styles={tourStyles}
        callback={handleJoyrideCallback}
        disableCloseOnEsc
        locale={locale}
        floaterProps={floaterProps}
      />

      <OnboardingDialog
        open={showStep1Modal}
        onAction={() => {
          setShowStep1Modal(false);
          completedStepsRef.current++;
          setShowChecklist(true);
          setTimeout(() => {
            setShowChecklist(false);
            setShowTour(true);
          }, 3000);
        }}
        title={modalTitle}
        description="Here is your Profile page where you provide your information for us to scan data brokers. The more details you provide, the better we can scan data broker sites for your personal information."
        description2="Providing additional names, cities, and states enables us to scan more thoroughly for your information."
        actionLabel="Continue"
      />

      <OnboardingDialog
        open={showStep2Modal}
        onAction={() => {
          setShowStep2Modal(false);
          setShowChecklist(true);
          completedStepsRef.current++;
          setTimeout(() => {
            setShowChecklist(false);
            setShowTour(true);
          }, 3000);
        }}
        title=""
        description="mePrism is committed to protecting your privacy. We'll only use your personal information to scan for exposed data, request removals from data brokers, and occasionally share updates about mePrism products or services we think might be helpful to you."
        actionLabel="Continue"
      >
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: { xs: 15, sm: 16, md: 18 },
            color: '#444',
            textAlign: 'center',
            mb: 4
          }}
        >
          We do not sell or share your data. We are your privacy agent.
        </Typography>
      </OnboardingDialog>

      <OnboardingDialog
        open={showFinalModal}
        onAction={onModalAction}
        title={modalTitle}
        description={modalDescription}
        actionLabel={modalActionLabel}
      />

      <AppTourChecklist
        open={showChecklist}
        progress={(completedStepsRef.current * 100) / checklistSteps.length}
        steps={checklistSteps}
        onClose={() => {
          setShowChecklist(false);
          setShowTour(true);
        }}
      />
    </>
  );
};

ProfileAppTourComponent.displayName = 'ProfileAppTour';

export const ProfileAppTour = React.memo(ProfileAppTourComponent);
